import {
  useMemo, useReducer, useCallback, useEffect, useRef, useState,
} from 'react';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';

// Reducers
import selectedRowsReducer from './tableReducer';

// UseCases
import {
  generateTableColumns,
  transformTableGroupData,
  areAllGroupRowsSelected,
  getEditableRows,
  getEditableRowsOrdered,
  computeSelectionRange,
  calculateSelectedRowsCount,
  buildSelectedRowsFromIds,
  selectionNeedsRehydrate,
  isAllRowsSelectionIndeterminate,
  isGroupRowsSelectionIndeterminate as computeGroupRowsSelectionIndeterminate,
} from './tableUseCase';

/** @type {ApiManagerTableColumns} */
const STATIC_CHECKBOX_COLUMN = {
  id: 'static-checkbox',
  groupId: null,
  type: 'static-checkbox',
  lib: '',
  width: '32px',
  align: 'left',
  verticalAlign: 'middle',
  bold: false,
  italic: false,
  sortable: false,
  headers: [{
    lib: '',
    width: '32px',
    colspan: 1,
    align: 'center',
    backgroundColor: null,
    textDecoration: { strikeThrough: false, underline: false },
  }],
  validation: {
    maxLength: null,
    required: 'non',
    regex: null,
  },
  statValue: '',
};

/**
 * @typedef useTableReturn
 * @property {TableInstance} table - Main table instance with all flattened data
 * @property {TableColumnsReactTable[]} tableColumns
 * @property {TableGroupsCustom[]} tableGroups
 * @property {(groupId: string) => import('@tanstack/react-table').Row<TableRowFlattened>[]} getRowsByGroupId - Get rows for a specific group
 * @property {(statsValue: ApiManagerTableGroupStatValue[]) => TableGroupStatsValue} getGroupStatsValueByColumnId - Get group stats value by column id
 * @property {import('./tableReducer').SelectedRowsState} selectedRows
 * @property {string[]} selectedRowsArray
 * @property {number} selectedRowsCount
 * @property {boolean} allRowsAreSelected - All rows are selected
 * @property {boolean} selectAllRowsIndeterminate - Partial selection across editable rows (header checkbox)
 * @property {(group: TableGroupsCustom) => boolean} areAllGroupRowsSelected
 * @property {(group: TableGroupsCustom) => boolean} isGroupRowsSelectionIndeterminate
 * @property {() => void} handleConfirmCloseBatchProcessing - The handle confirm close batch processing
 * @property {() => void} handleSelectAllRows - Handle select all rows
 * @property {(group: TableGroupsCustom) => void} handleSelectGroupRows
 * @property {(groupId: string, id: string, options?: { shiftKey?: boolean }) => void} handleSelectRowCallback
 * @property {() => void} handleDeselectAndCloseBatchProcessing - Handle deselect and close batch processing
 * @property {TableActiveCell | null} activeCell - Currently editing cell (at most one)
 */

/**
 * Use table
 * @param {object} props - The props
 * @param {Array<ApiManagerTableColumns>} props.columns - The table columns
 * @param {Array<ApiManagerTableGroups>} props.groups - The table groups
 * @param {string[]} props.columnsPinnedById - The columns pinned by id
 * @param {boolean} props.isLoading - The loading state
 * @param {boolean} props.batchProcessingIsOpen - The batch processing is open
 * @param {(isOpen: boolean) => void} props.setBatchProcessingIsOpen - The set batch processing is open
 * @param {(force?: boolean) => boolean} props.handleCloseBatchProcessing - The handle close batch processing
 * @param {import('@tanstack/react-table').SortingState} [props.sorting] - The sorting state
 * @param {(sorting: import('@tanstack/react-table').SortingState) => void} [props.setSorting] - The sorting state
 * @param {number} [props.tableWrapperWidth] - The width of the table wrapper in pixels
 * @param {(table: TableInstance) => void} [props.onColumnResizeEnd] - Callback fired when a column resize ends
 * @returns {useTableReturn} The table
 */
const useTableGroups = ({
  columns,
  groups,
  columnsPinnedById,
  isLoading,
  batchProcessingIsOpen,
  setBatchProcessingIsOpen,
  handleCloseBatchProcessing,
  sorting = [],
  setSorting = () => {},
  tableWrapperWidth = 0,
  onColumnResizeEnd,
}) => {
  // Selected rows
  const [selectedRows, dispatchSelectedRows] = useReducer(
    selectedRowsReducer,
    {},
  );
  const selectedRowsCount = useMemo(
    () => calculateSelectedRowsCount(selectedRows),
    [selectedRows],
  );

  const [activeCell, setActiveCellState] = useState(/** @type {TableActiveCell | null} */ (null));
  const activeCellRef = useRef(/** @type {TableActiveCell | null} */ (null));
  // One activation gesture opens the editor once: a remount of the active cell
  // (e.g. row moved by a refetch after save) must not reopen it.
  const activeCellOpenedRef = useRef(false);
  const setActiveCell = useCallback((/** @type {TableActiveCell} */ cell) => {
    activeCellRef.current = cell;
    activeCellOpenedRef.current = false;
    setActiveCellState(cell);
  }, []);
  const clearActiveCell = useCallback(() => {
    activeCellRef.current = null;
    activeCellOpenedRef.current = false;
    setActiveCellState(null);
  }, []);
  const isCellActive = useCallback(
    (/** @type {string} */ rowId, /** @type {string} */ columnId) => (
      activeCellRef.current?.rowId === rowId && activeCellRef.current?.columnId === columnId
    ),
    [],
  );
  const markActiveCellOpened = useCallback(() => {
    activeCellOpenedRef.current = true;
  }, []);
  const isActiveCellOpened = useCallback(() => activeCellOpenedRef.current, []);

  const tableMetaRef = useRef(/** @type {TableInstanceMeta} */ ({
    setActiveCell,
    clearActiveCell,
    isCellActive,
    markActiveCellOpened,
    isActiveCellOpened,
  }));
  tableMetaRef.current.setActiveCell = setActiveCell;
  tableMetaRef.current.clearActiveCell = clearActiveCell;
  tableMetaRef.current.isCellActive = isCellActive;
  tableMetaRef.current.markActiveCellOpened = markActiveCellOpened;
  tableMetaRef.current.isActiveCellOpened = isActiveCellOpened;

  /**
   * @description The table groups to display multi table
   */
  const tableGroups = useMemo(
    () => {
      if (!groups?.length || !columns?.length) return [];
      // Hide groups without rows: the API sends an empty group even when the table has no data
      return transformTableGroupData(groups, columns).filter((group) => group.data.length > 0);
    },
    [groups, columns],
  );

  // RESELECT ROWS AFTER BATCH PROCESSING
  // Some batch processing actions may change groups, so we need to reselect the rows after the batch processing is done.
  // When groups change, we check if some selected rows are not present in the new groups, and if so, we reselect them.
  useEffect(() => {
    const ids = Object.values(selectedRows).flatMap((group) => Object.keys(group));
    if (!ids.length || !tableGroups.length) return;
    if (!selectionNeedsRehydrate(selectedRows, tableGroups)) return;
    const newState = buildSelectedRowsFromIds(ids, tableGroups);
    dispatchSelectedRows({ type: 'RESTORE_SELECTION', payload: { newState } });
  }, [tableGroups, selectedRows]);

  /** @type {import('react').MutableRefObject<{ groupId: string, id: string } | null>} */
  const selectionAnchorRef = useRef(null);

  useEffect(() => {
    selectionAnchorRef.current = null;
  }, [tableGroups]);

  /**
   * @description Flatten all group data into a single array for the main table instance
   */
  const flattenedTableData = useMemo(
    () => {
      if (!tableGroups.length) return [];
      return tableGroups.flatMap((group) => group.data.map((row) => ({
        ...row,
        $groupId: group.id, // Add group id to each row for filtering later
        $groupLib: group.lib, // Add group label for reference
      })));
    },
    [tableGroups],
  );

  // Group selection map - Only for groups with editable rows
  const groupSelectionMap = useMemo(() => tableGroups.reduce(
    (
      /** @type {Record<string, boolean>} */ acc,
      group,
    ) => {
      const editableRows = getEditableRows(group);
      if (editableRows.length > 0) {
        acc[group.id] = areAllGroupRowsSelected(group, selectedRows);
      }
      return acc;
    },
    /** @type {Record<string, boolean>} */ ({}),
  ), [tableGroups, selectedRows]);

  // Selected Rows - Only check groups that have editable rows
  const allRowsAreSelected = useMemo(() => {
    if (tableGroups.length === 0) return false;
    const groupsWithEditableRows = tableGroups.filter((group) => {
      const editableRows = getEditableRows(group);
      return group.data.length > 0 && editableRows.length > 0;
    });
    if (groupsWithEditableRows.length === 0) return false;
    return Object.values(groupSelectionMap).every(Boolean);
  }, [groupSelectionMap, tableGroups]);

  const selectAllRowsIndeterminate = useMemo(
    () => isAllRowsSelectionIndeterminate(tableGroups, selectedRows),
    [tableGroups, selectedRows],
  );

  const isGroupRowsSelectionIndeterminate = useCallback(
    /**
     * @param {TableGroupsCustom} group
     * @returns {boolean}
     */
    (group) => computeGroupRowsSelectionIndeterminate(group, selectedRows),
    [selectedRows],
  );

  /**
   * @description Handle select all rows
   */
  const handleSelectAllRows = () => {
    if (allRowsAreSelected && batchProcessingIsOpen) {
      const confirmed = handleCloseBatchProcessing();
      if (!confirmed) return;
    }

    const groupsData = tableGroups.map((group) => ({
      ...group,
      data: getEditableRows(group),
    }));

    dispatchSelectedRows({
      type: allRowsAreSelected ? 'DESELECT_ALL_ROWS' : 'SELECT_ALL_ROWS',
      payload: {
        groups: groupsData,
      },
    });
  };

  /**
   * Handle select group rows
   * @param {TableGroupsCustom} group - The group
   */
  const handleSelectGroupRowsImpl = (group) => {
    const isAllRowsSelected = areAllGroupRowsSelected(group, selectedRows);
    const count = calculateSelectedRowsCount(selectedRows);
    const groupCount = calculateSelectedRowsCount(selectedRows, group.id);

    if (isAllRowsSelected && count === groupCount && batchProcessingIsOpen) {
      const confirmed = handleCloseBatchProcessing();
      if (confirmed) {
        dispatchSelectedRows({
          type: 'DESELECT_GROUP_ROWS',
          payload: {
            groupId: group.id,
            rows: getEditableRows(group),
          },
        });
      }
      return;
    }

    dispatchSelectedRows({
      type: isAllRowsSelected ? 'DESELECT_GROUP_ROWS' : 'SELECT_GROUP_ROWS',
      payload: {
        groupId: group.id,
        rows: getEditableRows(group),
      },
    });
  };

  /**
   * Handle select row
   * @param {string} groupId - The group id
   * @param {string} id - The id
   * @param {{ shiftKey?: boolean }} [options]
   */
  const handleSelectRowImpl = (groupId, id, options = {}) => {
    const { shiftKey = false } = options;
    const orderedEditable = getEditableRowsOrdered(tableGroups);
    const anchor = selectionAnchorRef.current;
    const anchorStillValid = Boolean(
      anchor && orderedEditable.some((r) => r.groupId === anchor.groupId && r.id === anchor.id),
    );
    const sameAsAnchor = Boolean(
      anchor && anchor.groupId === groupId && anchor.id === id,
    );

    if (shiftKey && anchorStillValid && !sameAsAnchor) {
      const rangeRows = computeSelectionRange(
        orderedEditable,
        /** @type {{ groupId: string, id: string }} */ (anchor),
        { groupId, id },
      );
      if (rangeRows.length > 0) {
        dispatchSelectedRows({
          type: 'SELECT_ROWS_RANGE',
          payload: { rangeRowRefs: rangeRows },
        });
        selectionAnchorRef.current = { groupId, id };
      }
      return;
    }

    const isSelected = selectedRows[groupId]?.[id] ?? false;
    const count = calculateSelectedRowsCount(selectedRows);

    // Close batch processing and deselect row if selected and only one row selected
    if (isSelected && count === 1) {
      if (batchProcessingIsOpen) {
        const confirmed = handleCloseBatchProcessing();
        if (!confirmed) return;
      }
      dispatchSelectedRows({
        type: isSelected ? 'DESELECT_ROW' : 'SELECT_ROW',
        payload: { groupId, id },
      });
      selectionAnchorRef.current = { groupId, id };
      return;
    }

    dispatchSelectedRows({
      type: isSelected ? 'DESELECT_ROW' : 'SELECT_ROW',
      payload: { groupId, id },
    });
    selectionAnchorRef.current = { groupId, id };
  };

  const handleConfirmCloseBatchProcessing = useCallback(() => {
    handleCloseBatchProcessing();
  }, [handleCloseBatchProcessing]);

  const handleDeselectAllRows = () => {
    dispatchSelectedRows({
      type: 'DESELECT_ALL_ROWS',
      payload: {},
    });
  };

  const handleDeselectAndCloseBatchProcessing = () => {
    handleDeselectAllRows();
    setBatchProcessingIsOpen(false);
  };

  const handleSelectRowRef = useRef(handleSelectRowImpl);
  handleSelectRowRef.current = handleSelectRowImpl;
  const handleSelectGroupRowsRef = useRef(handleSelectGroupRowsImpl);
  handleSelectGroupRowsRef.current = handleSelectGroupRowsImpl;

  const handleSelectRowCallback = useCallback(
    /**
     * @param {string} groupId - The group id
     * @param {string} id - The id
     * @param {{ shiftKey?: boolean }} [opts]
     */
    (groupId, id, opts) => {
      handleSelectRowRef.current(groupId, id, opts);
    },
    [],
  );

  const handleSelectGroupRows = useCallback(
    /**
     * @param {TableGroupsCustom} group
     */
    (group) => {
      handleSelectGroupRowsRef.current(group);
    },
    [],
  );

  /**
   * @description The table columns generated from the columns
   */
  const tableColumns = useMemo(
    () => {
      if (!columns?.length || isLoading) return [];
      return generateTableColumns([STATIC_CHECKBOX_COLUMN, ...columns], tableWrapperWidth);
    },
    [columns, isLoading, tableWrapperWidth],
  );

  // Single table instance with all flattened data
  const table = useReactTable({
    data: flattenedTableData,
    columns: tableColumns,
    columnResizeMode: 'onChange',
    columnResizeDirection: 'ltr',
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    state: {
      sorting,
      columnPinning: {
        left: [STATIC_CHECKBOX_COLUMN.id, ...columnsPinnedById],
      },
    },
    meta: tableMetaRef.current,
    debugTable: false,
    debugHeaders: false,
    debugColumns: false,
  });

  // Detect column resize end.
  // TanStack Table has no built-in onResizeEnd callback. With columnResizeMode: 'onChange',
  // isResizingColumn holds the column id during drag, then switches to false on mouse release.
  // We track this transition (truthy → falsy) to fire the callback exactly once per resize.
  /** @type {import('react').MutableRefObject<string | false>} */
  const prevIsResizingRef = useRef(false);
  const { isResizingColumn } = table.getState().columnSizingInfo;

  useEffect(() => {
    if (prevIsResizingRef.current && !isResizingColumn) {
      onColumnResizeEnd?.(table);
    }
    prevIsResizingRef.current = isResizingColumn;
  }, [isResizingColumn, onColumnResizeEnd]);

  /**
   * Get rows for a specific group from the main table instance
   * @param {string} groupId - The group id
   * @returns {import('@tanstack/react-table').Row<TableRowFlattened>[]} The rows for the group
   */
  const getRowsByGroupId = useCallback(
    /**
     * @param {string} groupId
     * @returns {import('@tanstack/react-table').Row<TableRowFlattened>[]}
     */
    (groupId) => table.getRowModel().rows.filter((row) => row.original.$groupId === groupId),
    [table],
  );

  /**
   * Get the selected rows array
   * @returns {string[]} The selected rows array
   */
  const selectedRowsArray = useMemo(() => Object.values(selectedRows)
    .flatMap((group) => Object.keys(group)), [selectedRows]);

  /**
   * Get the group stats value by columnId
   */
  const getGroupStatsValueByColumnId = useCallback(
    /**
     * @param {ApiManagerTableGroupStatValue[]} statsValue
     * @returns {TableGroupStatsValue}
     */
    (statsValue) => {
      if (!statsValue?.length) return {};
      return statsValue.reduce((
        /** @type {TableGroupStatsValue} */ acc,
        /** @type {ApiManagerTableGroupStatValue} */ statValue,
      ) => {
        acc[statValue.columnId] = statValue;
        return acc;
      }, {});
    },
    [tableGroups],
  );

  return {
    table,
    tableGroups,
    tableColumns,
    getRowsByGroupId,
    getGroupStatsValueByColumnId,

    // Selected rows
    selectedRows,
    selectedRowsArray,
    selectedRowsCount,
    allRowsAreSelected,
    selectAllRowsIndeterminate,
    areAllGroupRowsSelected: (group) => areAllGroupRowsSelected(group, selectedRows),
    isGroupRowsSelectionIndeterminate,

    // Handlers
    handleConfirmCloseBatchProcessing,
    handleSelectAllRows,
    handleSelectGroupRows,
    handleSelectRowCallback,
    handleDeselectAndCloseBatchProcessing,

    activeCell,
  };
};

export default useTableGroups;
